import { QueryParameter } from 'app/constants/QueryParameter';
import DynamicRoute from 'app/constants/Routes/DynamicRoute';
import { IntegerRange } from 'app/constants/schemas';
import { History, IHistoryLocation } from 'app/services/history';
import { parse, StringifiableRecord, stringify } from 'query-string';
import { LanguageId } from 'app/constants/enums/Language/data';

export const enum QueryUpdater {
  Push = 'push',
  Replace = 'replace',
}

export interface ISetQueryOptions {
  updater: QueryUpdater;
  state?: IHistoryLocation['state'];
}

const DefaultOptions: ISetQueryOptions = { updater: QueryUpdater.Push };

/**
 * Helper to add an item to a query parameter that is an array.
 * @returns new value to be passed to mergeQuery or setQuery
 */
const appendArray = (history: History, field: string, value: string) => {
  const query = parse(history.location.search);
  const current = query[field];

  if (Array.isArray(current)) {
    return [...current, value];
  } else if (typeof current !== 'undefined') {
    return [current, value];
  } else {
    return [value];
  }
};

/**
 * Helper to filter an item from a query parameter that is an array.
 * @returns new value to be passed to mergeQuery or setQuery
 */
const filterArray = (history: History, field: string, value: string) => {
  const query = parse(history.location.search);
  const current = query[field];

  if (Array.isArray(current)) {
    return current.filter(v => v !== value);
  } else if (current !== value) {
    return [current];
  } else {
    return undefined;
  }
};

const makeAddToArray = (field: string) => (history: History, value: string) => () => {
  const values = appendArray(history, field, value);
  return mergeQuery(history, { [field]: values });
};

const makeRemoveFromArray = (field: string) => (history: History, value: string) => () => {
  const values = filterArray(history, field, value);
  return mergeQuery(history, { [field]: values });
};

// Thunks that update the route.
export const updateLanguage = (
  history: History,
  route: DynamicRoute<{ languageId: LanguageId }>,
  language: LanguageId,
) => () => setParams(history, route, { languageId: language }, { updater: QueryUpdater.Replace });

export const updateSearchQuery = (history: History, query: string) => () => mergeQuery(history, { query });
export const updateOrder = (history: History, order: string) => () => mergeQuery(history, { order });

export const addTag = makeAddToArray('tags');
export const removeTag = makeRemoveFromArray('tags');
export const addInternalTag = makeAddToArray('internal_tags');
export const removeInternalTag = makeRemoveFromArray('internal_tags');
export const addBrand = makeAddToArray('brands');
export const removeBrand = makeRemoveFromArray('brands');
export const addDictionaryTag = (history: History, option: string, value: boolean | undefined) => () =>
  mergeQuery(history, { [option]: value });
export const removeDictionaryTag = (history: History, option: string) => () =>
  mergeQuery(history, { [option]: undefined });

export const addWordOption = (history: History, option: string, value: boolean | undefined) => () =>
  mergeQuery(history, { [option]: value });

export const removeWordOption = (history: History, option: string) => () =>
  mergeQuery(history, { [option]: undefined });

export const addLessonStatus = (history: History, statusId: string, value: boolean | undefined) => () =>
  mergeQuery(history, { [statusId]: value });

export const removeLessonStatus = (history: History, statusId: string) => () =>
  mergeQuery(history, { [statusId]: undefined });

export const updateWithPreservedParams = (history: History, params: StringifiableRecord) => () =>
  mergeQuery(history, params, { updater: QueryUpdater.Replace });

export const updateDailyNewsSearchCategory = (history: History, category: string) => () =>
  mergeQuery(history, { tags: category ? [category] : undefined });

export const updateStaffTeam = (history: History, team: string) => () =>
  mergeQuery(history, { staff_team: team });

export const removeLanguageParam = (history: History) => () =>
  mergeQuery(history, { [QueryParameter.Language]: undefined }, { updater: QueryUpdater.Replace });

export const addUserStatus = (history: History, statusId: string, value: string) => () =>
  mergeQuery(history, { [statusId]: value });

export const removeUserStatus = (history: History, statusId: string) => () =>
  mergeQuery(history, { [statusId]: undefined });

export const updateLevelRange = (history: History, integerRange: IntegerRange) => () =>
  mergeQuery(history, { min_level: integerRange.min, max_level: integerRange.max });

export const updateMarket = (history: History, market: string) => () =>
  mergeQuery(history, { market });

// Helpers
const setParams = <P extends { [index: string]: unknown }>(
  history: History,
  route: DynamicRoute<P>,
  params: P,
  { updater, ...restOptions }: ISetQueryOptions = DefaultOptions,
) => {
  const pathname = route.getPathname(params);

  history[updater]({
    ...history.location,
    pathname,
    ...restOptions,
  });
};

const setQuery = (
  history: History,
  query: StringifiableRecord,
  { updater, ...restOptions }: ISetQueryOptions = DefaultOptions,
) => {
  history[updater]({
    ...history.location,
    search: stringify(removeEmptyQueryParams(query)),
    ...restOptions,
  });
};

export const mergeQuery = (history: History, query: StringifiableRecord, options?: ISetQueryOptions) => {
  const oldQuery = { ...parse(history.location.search) };
  const newQuery = removeEmptyQueryParams({ ...oldQuery, ...query });

  setQuery(history, newQuery, options);
};

const removeEmptyQueryParams = (query: StringifiableRecord): StringifiableRecord => {
  const queryClone: StringifiableRecord = { ...query };

  Object.keys(queryClone).forEach(key => {
    if (queryClone[key] === undefined) {
      delete queryClone[key];
    }
  });

  return queryClone;
};
